<template>
    <div class="profile-page">
        <LoadingBar v-if="loading" message="Loading Team..." />

        <div v-else class="container-xl">

            <!-- Breadcrumb -->
            <nav class="profile-breadcrumb" aria-label="breadcrumb">
                <a href="#" @click.prevent="$router.push('/team/list')">
                    <i class="fa fa-arrow-left"></i> Teams
                </a>
                <span class="sep">/</span>
                <span class="current">{{ team.name || 'Team' }}</span>
            </nav>

            <!-- Header -->
            <div class="profile-header">
                <div class="profile-header-icon" style="background:var(--pp-info-light);color:var(--pp-info);">
                    <span style="font-size:28px;font-weight:600;">
                        {{ initials }}
                    </span>
                </div>
                <div class="profile-header-body">
                    <h3>{{ team.name || 'Unnamed Team' }}</h3>
                    <div class="profile-header-meta">
                        <span><i class="fa fa-hashtag"></i>{{ team.external_id || 'N/A' }}</span>
                        <span v-if="head"><i class="fa fa-user-tie"></i>{{ head.first_name }} {{ head.last_name }}</span>
                        <span><i class="fa fa-users"></i>{{ members.length }} Member{{ members.length === 1 ? '' : 's' }}</span>
                    </div>
                </div>
                <div class="profile-actions">
                    <span class="profile-badge c-success">
                        <span class="dot"></span> Active
                    </span>
                    <button type="button" class="action-btn action-btn-back" @click="$router.push('/team/list')">
                        <i class="fa fa-arrow-left"></i> Back
                    </button>
                    <button type="button" class="action-btn action-btn-primary" @click.prevent="editTeam(team)">
                        <i class="fa fa-pencil"></i> Edit
                    </button>
                </div>
            </div>

            <!-- Stat bar -->
            <div class="profile-stats">
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-info">
                        <i class="fa fa-users"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Total Members</div>
                        <div class="profile-stat-value">{{ members.length }}</div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-accent">
                        <i class="fa fa-user-tie"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Team Head</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ head ? head.first_name + ' ' + head.last_name : 'N/A' }}
                        </div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-warning">
                        <i class="fa fa-calendar"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Created</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ formatDate(team.created_at) || 'N/A' }}
                        </div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-success">
                        <i class="fa fa-refresh"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Last Updated</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ formatDate(team.updated_at) || 'N/A' }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main layout -->
            <div class="row g-3">

                <!-- Left: Team details -->
                <div class="col-lg-8 d-flex flex-column gap-3">

                    <!-- Core details -->
                    <div class="profile-card">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-info-circle"></i> Team Details
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-field-grid">
                                <div class="profile-field" v-if="team.external_id">
                                    <span class="profile-label">External ID</span>
                                    <span class="profile-value" style="font-family:monospace;">
                                        {{ team.external_id }}
                                    </span>
                                </div>
                                <div class="profile-field" v-if="head">
                                    <span class="profile-label">Team Head</span>
                                    <span class="profile-value">
                                        <span class="profile-badge c-warning">
                                            <i class="fa fa-crown me-1"></i>
                                            {{ head.first_name }} {{ head.last_name }}
                                        </span>
                                    </span>
                                </div>
                                <div class="profile-field" v-if="head?.email">
                                    <span class="profile-label">Head Email</span>
                                    <span class="profile-value">
                                        <a :href="`mailto:${head.email}`" style="color:var(--pp-accent);text-decoration:none;">
                                            {{ head.email }}
                                        </a>
                                    </span>
                                </div>
                                <div class="profile-field" v-if="head?.phone">
                                    <span class="profile-label">Head Phone</span>
                                    <span class="profile-value">
                                        <a :href="`tel:${head.phone}`" style="color:var(--pp-accent);text-decoration:none;">
                                            {{ head.phone }}
                                        </a>
                                    </span>
                                </div>
                                <div class="profile-field" v-if="team.created_at">
                                    <span class="profile-label">Created At</span>
                                    <span class="profile-value">{{ formatDate(team.created_at) }}</span>
                                </div>
                                <div class="profile-field" v-if="team.updated_at">
                                    <span class="profile-label">Last Updated</span>
                                    <span class="profile-value">{{ formatDate(team.updated_at) }}</span>
                                </div>
                            </div>

                            <!-- Description: full-width field -->
                            <div class="profile-field mt-3" v-if="team.description"
                                style="grid-column:1/-1;">
                                <span class="profile-label">Description</span>
                                <span class="profile-value" style="line-height:1.6;">
                                    {{ team.description }}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Right: Members sidebar -->
                <div class="col-lg-4 d-flex flex-column gap-3">

                    <!-- Members -->
                    <div class="profile-card" v-if="members.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-info);">
                                <i class="fa fa-users" style="color:var(--pp-info);"></i> Members
                            </div>
                            <span class="profile-count-pill">{{ members.length }}</span>
                        </div>
                        <div class="profile-card-body" style="max-height:400px;overflow-y:auto;">
                            <div class="d-flex flex-column gap-2">
                                <div v-for="member in members" :key="member.id"
                                    class="d-flex align-items-center gap-2 profile-field"
                                    style="flex-direction:row;padding:10px 12px;">
                                    <div class="profile-avatar c-info" style="width:28px;height:28px;font-size:11px;">
                                        {{ memberInitials(member) }}
                                    </div>
                                    <div style="flex:1;">
                                        <span class="profile-value" style="font-size:13px;display:block;">
                                            {{ member.first_name }} {{ member.last_name }}
                                            <span v-if="isHead(member)" 
                                                class="profile-badge c-warning" 
                                                style="font-size:9px;padding:1px 6px;margin-left:4px;">
                                                <i class="fa fa-crown"></i> Head
                                            </span>
                                        </span>
                                        <span style="font-size:11px;color:var(--pp-text-muted);display:block;">
                                            {{ member.role || 'Member' }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Contact info -->
                    <div class="profile-card" v-if="head">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-accent);">
                                <i class="fa fa-address-card" style="color:var(--pp-accent);"></i> Contact
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="d-flex flex-column gap-2">
                                <div v-if="head.email" class="profile-field" style="flex-direction:row;padding:8px 12px;">
                                    <i class="fa fa-envelope" style="width:20px;color:var(--pp-text-muted);"></i>
                                    <a :href="`mailto:${head.email}`" 
                                        style="color:var(--pp-accent);text-decoration:none;font-size:13px;">
                                        {{ head.email }}
                                    </a>
                                </div>
                                <div v-if="head.phone" class="profile-field" style="flex-direction:row;padding:8px 12px;">
                                    <i class="fa fa-phone" style="width:20px;color:var(--pp-text-muted);"></i>
                                    <a :href="`tel:${head.phone}`" 
                                        style="color:var(--pp-accent);text-decoration:none;font-size:13px;">
                                        {{ head.phone }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Empty state -->
                    <div class="profile-card" v-if="!members.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-users"></i> Members
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-empty">
                                <i class="fa fa-users"></i>
                                <p>No members yet</p>
                                <span style="font-size:12px;color:var(--pp-text-muted);">
                                    This team has no members assigned
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

            </div>

        </div>
    </div>
<FormModal ref="editTeamModal" title="Edit Team" id="edit_team_form_modal">
    <EditTeam 
        v-if="selectedTeam" 
        :external-id="selectedTeam.external_id" 
        @updated="onTeamUpdated"
        @close="closeEditModal('editTeamModal')" 
    />
</FormModal>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import EditTeam from './EditTeam.vue';
export default {
  name: 'TeamProfile',
    components: {
    LoadingBar,
    FormModal,
    EditTeam,
  },

  data() {
    return {
      team: {},
      loading: false,
       external_id: this.$route.params.external_id,
    }
  },

  computed: {
    head() {
      return this.team.get_head || null
    },
    members() {
      return this.team.get_members || []
    },
    initials() {
      const words = (this.team.name || 'T').trim().split(/\s+/)
      const f = words[0]?.[0] || ''
      const s = words[1]?.[0] || ''
      return (f + s).toUpperCase() || 'T'
    }
  },

  mounted() {
    this.fetchProfile()
  },

  methods: {
 formatDate(date) {
            if (!date) return '—'
            try {
                return new Date(date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            } catch {
                return date
            }
        },

        memberInitials(member) {
            if (!member) return '?'
            const first = member.first_name || ''
            const last = member.last_name || ''
            return (first.charAt(0) + last.charAt(0)).toUpperCase() || '?'
        },


    isHead(member) {
      return this.head && member.id === this.head.id
    },

    async fetchProfile() {
      this.loading = true
      const result = await form_api_submit({
        url: `team/${this.external_id}`,
        method: 'GET',
        show_success_toast: false,
        show_error_toast: true,
      })
      if (result.success) {
        this.team = result.data.team
      }
      this.loading = false
    },
      editTeam(team) {
      this.selectedTeam = team
      this.$refs.editTeamModal.showModal()
    },
    closeEditModal(ref) {
      if (this.$refs[ref]) {
        this.$refs[ref].closeModal()
      }
    },
 async onTeamUpdated() {
  setTimeout(async () => {
    await this.fetchProfile()
  }, 1000)
}
  }
}
</script>
